import { debugLog } from "./debug";

/** Cumulative token usage recorded for a single calendar day (local `YYYY-MM-DD`,
 * matching the ISO date prefix — see `todayKey`). */
export interface DailyUsageRecord {
  date: string;
  totalTokens: number;
}

export const USAGE_KEY = "aiAssistant.usage.v1";

/** Today's date as an ISO `YYYY-MM-DD` key — the local calendar day rolls the
 * counter over, matching how a user thinks about "today". */
function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Reads and parses the stored record; corrupt/absent storage → a fresh zeroed
 * record for today. Never throws. */
function readRecord(): DailyUsageRecord {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(USAGE_KEY);
  } catch (e) {
    debugLog("usage-store:read-failed", e instanceof Error ? e.name : "unknown");
    return { date: todayKey(), totalTokens: 0 };
  }
  if (!raw) return { date: todayKey(), totalTokens: 0 };
  try {
    const parsed = JSON.parse(raw) as Partial<DailyUsageRecord> | null;
    if (
      parsed &&
      typeof parsed.date === "string" &&
      typeof parsed.totalTokens === "number" &&
      Number.isFinite(parsed.totalTokens)
    ) {
      return { date: parsed.date, totalTokens: parsed.totalTokens };
    }
    return { date: todayKey(), totalTokens: 0 };
  } catch {
    return { date: todayKey(), totalTokens: 0 };
  }
}

/** Persists the record best-effort; a quota/disabled-storage failure is logged, not thrown. */
function writeRecord(record: DailyUsageRecord): void {
  try {
    localStorage.setItem(USAGE_KEY, JSON.stringify(record));
  } catch (e) {
    debugLog("usage-store:write-failed", e instanceof Error ? e.name : "unknown");
  }
}

/** Today's cumulative token total. A stored record from a previous day reads as
 * 0 — it is not cleared here (that only happens on the next `addDailyTokens`
 * write), just treated as stale. */
export function getDailyTokens(): number {
  const stored = readRecord();
  return stored.date === todayKey() ? stored.totalTokens : 0;
}

/** Adds `tokens` to today's cumulative total, rolling the counter over first if
 * the stored record is from a previous day, and returns the new total.
 * Non-positive/non-finite `tokens` is a no-op that just returns today's total. */
export function addDailyTokens(tokens: number): number {
  const today = todayKey();
  if (!Number.isFinite(tokens) || tokens <= 0) {
    const stored = readRecord();
    return stored.date === today ? stored.totalTokens : 0;
  }
  const stored = readRecord();
  const base = stored.date === today ? stored.totalTokens : 0;
  const next: DailyUsageRecord = { date: today, totalTokens: base + tokens };
  writeRecord(next);
  return next.totalTokens;
}
